// ReelViewer — full-screen overlay used to watch a reel with TikTok-style
// right-side action column, viewer/commenter avatars, playback controls
// (play/pause · rewind 10s · fast-fwd 10s · replay), plus like / comment /
// share (share = re-post the reel to my own newsfeed).
//
// Rendered as a fixed inset-0 z-[100] overlay so it works consistently on
// mobile and desktop. Purely additive — the reel post still renders inline
// in the feed with its own <video controls> (nothing about the existing
// inline playback changed). Openers can pass an optional `onShared` callback
// so they can toast/refresh once the share completes.
import { useEffect, useRef, useState } from "react";
import { X, Heart, MessageCircle, Share2, Play, Pause, Rewind, FastForward, RotateCcw, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import InteractorsModal from "./InteractorsModal";
import SponsorBanner from "./SponsorBanner";

// Compact relative-time formatter (e.g. "now", "2m", "5h", "3d") used by the
// comment thread so people can see how fresh a reply is at a glance.
function relTime(ts) {
  if (!ts) return "";
  try {
    const then = new Date(ts).getTime();
    const s = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (s < 30) return "now";
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60); if (m < 60) return `${m}m`;
    const h = Math.round(m / 60); if (h < 24) return `${h}h`;
    const d = Math.round(h / 24); if (d < 30) return `${d}d`;
    const mo = Math.round(d / 30); if (mo < 12) return `${mo}mo`;
    return `${Math.round(mo / 12)}y`;
  } catch { return ""; }
}

export default function ReelViewer({ reel, onClose, onShared }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [likes, setLikes] = useState(reel?.likes || 0);
  const [liked, setLiked] = useState(false);
  const [interactorsKind, setInteractorsKind] = useState(null);
  const [viewCount, setViewCount] = useState(reel?.view_count || 0);
  // Comment thread — visible sheet listing comment TEXT (not just counts) so people
  // can actually read the conversation. Toggled by tapping the comment button in the
  // right-side action column. Feb 2026.
  const [commentsOpen, setCommentsOpen] = useState(false);

  useEffect(() => {
    if (!reel?.id) return;
    // Fetch existing comments so the avatar column has real faces.
    api.get(`/posts/${reel.id}/comments`).then(({ data }) => setComments(data || [])).catch(() => setComments([]));
    // Log a view impression once the reel opens.
    api.post(`/posts/${reel.id}/view`).catch(() => {});
    // Pull analytics so we can prime the eye-icon count.
    api.get(`/posts/${reel.id}/analytics`).then(({ data }) => {
      setLikes(data?.counts?.likes || 0);
      setLiked(!!data?.my_state?.liked);
      setViewCount(data?.counts?.views || 0);
    }).catch(() => {});
  }, [reel?.id]);

  // Keep the background audio track (if any) in sync with the video's own play/pause.
  useEffect(() => {
    const v = videoRef.current; const a = audioRef.current;
    if (!v) return;
    if (playing) { v.play?.().catch(() => {}); a?.play?.().catch(() => {}); }
    else         { v.pause?.();                  a?.pause?.(); }
  }, [playing]);

  if (!reel) return null;

  const isVideo = reel.kind === "video" || reel.kind === "reel" || (reel.media_url && /\.(mp4|webm|mov)$/i.test(reel.media_url));

  const seek = (delta) => {
    const v = videoRef.current; if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.duration || 0, v.currentTime + delta));
  };
  const replay = () => {
    const v = videoRef.current; const a = audioRef.current;
    if (!v) return;
    v.currentTime = 0; if (a) a.currentTime = 0;
    setPlaying(true);
  };

  const toggleLike = async () => {
    if (!user) { toast.error("Sign in to like"); return; }
    try {
      const { data } = await api.post(`/posts/${reel.id}/like`);
      const active = data?.active ?? data?.liked ?? false;
      setLiked(!!active);
      setLikes((n) => Math.max(0, n + (active ? 1 : -1)));
    } catch { toast.error("Like failed"); }
  };

  const sendComment = async () => {
    const txt = commentText.trim();
    if (!txt) return;
    try {
      const { data } = await api.post(`/posts/${reel.id}/comment`, { text: txt });
      setComments((cs) => [...cs, data]);
      setCommentText("");
    } catch { toast.error("Comment failed"); }
  };

  const shareToFeed = async () => {
    if (!user) { toast.error("Sign in to share"); return; }
    try {
      await api.post("/posts/v2", {
        content: `Reposted a reel by ${reel.user_name}${reel.caption ? " — " + reel.caption : ""}`,
        kind: reel.kind || "reel",
        media_url: reel.media_url,
        source: "reshare",
      });
      // Also fire the existing share endpoint so counters stay accurate.
      api.post(`/posts/${reel.id}/share`).catch(() => {});
      // Reloading whatever profile is currently open (often someone else's) never
      // shows the repost, since it's created under the sharer's own id -- a
      // successful share looked like it silently did nothing. Send the sharer
      // straight to their own profile so they can actually see it landed.
      toast.success("Reel shared to your newsfeed", {
        action: { label: "View", onClick: () => navigate(`/u/${user.id}`) },
      });
      onClose?.();
      onShared?.();
    } catch { toast.error("Share failed"); }
  };

  // Viewer column: dedupe by user id so a chatty commenter isn't stacked 5x.
  const avatarUsers = [];
  const seen = new Set();
  for (const c of comments) {
    if (!seen.has(c.user_id)) { seen.add(c.user_id); avatarUsers.push({ id: c.user_id, name: c.user_name, avatar: c.user_avatar }); }
    if (avatarUsers.length >= 12) break;
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center" data-testid="reel-viewer" role="dialog" aria-modal="true">
      <button onClick={onClose} className="absolute top-3 right-3 z-10 bg-black/60 hover:bg-white/20 w-9 h-9 rounded-full flex items-center justify-center text-white" data-testid="reel-viewer-close" aria-label="Close reel"><X className="w-5 h-5" /></button>

      <div className="relative w-full h-full sm:w-auto sm:h-[92vh] sm:aspect-[9/16] bg-black overflow-hidden" onClick={() => setPlaying((p) => !p)}>
        {isVideo ? (
          <video
            ref={videoRef}
            src={reel.media_url}
            className="w-full h-full object-contain"
            autoPlay
            playsInline
            loop={false}
            onEnded={() => setPlaying(false)}
            data-testid="reel-viewer-video"
          />
        ) : (
          <img src={reel.media_url} alt="" className="w-full h-full object-contain" data-testid="reel-viewer-image" />
        )}
        {reel.background_audio_url && <audio ref={audioRef} src={reel.background_audio_url} autoPlay loop />}

        {/* Header attribution */}
        <div className="absolute top-3 left-3 flex items-center gap-2 pointer-events-none z-10">
          <img src={reel.user_avatar} alt="" className="w-8 h-8 rounded-full ring-1 ring-white/40" />
          <div>
            <div className="text-white text-xs font-semibold" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.9)" }}>{reel.user_name}</div>
            {reel.ts && <div className="text-white/70 text-[10px]" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.9)" }}>{new Date(reel.ts).toLocaleString()}</div>}
          </div>
        </div>

        {/* Playback controls -- more transparent per owner feedback, nudged up
            slightly so it never sits flush against the bottom edge/gesture bar. */}
        <div className="absolute inset-x-0 bottom-4 flex items-center justify-center gap-2 z-10" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => seek(-10)} className="bg-black/35 hover:bg-black/55 w-9 h-9 rounded-full flex items-center justify-center text-white" data-testid="reel-rewind" aria-label="Rewind 10 seconds"><Rewind className="w-4 h-4" /></button>
          <button onClick={() => setPlaying((p) => !p)} className="bg-black/45 hover:bg-black/65 w-11 h-11 rounded-full flex items-center justify-center text-white" data-testid="reel-play-pause" aria-label={playing ? "Pause" : "Play"}>{playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}</button>
          <button onClick={() => seek(10)} className="bg-black/35 hover:bg-black/55 w-9 h-9 rounded-full flex items-center justify-center text-white" data-testid="reel-fast-forward" aria-label="Fast-forward 10 seconds"><FastForward className="w-4 h-4" /></button>
          <button onClick={replay} className="bg-black/35 hover:bg-black/55 w-9 h-9 rounded-full flex items-center justify-center text-white" data-testid="reel-replay" aria-label="Replay from start"><RotateCcw className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Right-side action column — viewer/commenter avatars + like / comment / share
          Same visual pattern as the live-stream viewer overlay on Watch: small
          transparent stack, clickable, gently glowing when the viewer count is high. */}
      <div className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-4 z-10" onClick={(e) => e.stopPropagation()} data-testid="reel-action-column">
        <button onClick={() => setInteractorsKind("like")} className="flex flex-col items-center gap-1" data-testid="reel-interactors-open" title="Who liked / viewed / shared">
          <div className="w-11 h-11 rounded-full bg-black/70 hover:bg-black/80 flex items-center justify-center border border-white/15">
            <Eye className="w-5 h-5" style={{ color: "#fff", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }} />
          </div>
          <span className="text-[10px] text-white font-bold drop-shadow">{viewCount.toLocaleString()}</span>
        </button>
        <button onClick={toggleLike} className="flex flex-col items-center gap-1" data-testid="reel-like">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center border border-white/15 ${liked ? "bg-nexus-crimson/70" : "bg-black/70 hover:bg-black/80"}`}>
            <Heart className="w-5 h-5" style={{ color: "#fff", fill: liked ? "#ff2e8a" : "none", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }} />
          </div>
          <span className="text-[10px] text-white font-bold drop-shadow">{likes}</span>
        </button>
        <button onClick={() => setCommentsOpen((v) => !v)} className="flex flex-col items-center gap-1" data-testid="reel-comment-btn" aria-label="Toggle comments">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center border border-white/15 ${commentsOpen ? "bg-nexus-cyan/70" : "bg-black/70 hover:bg-black/80"}`}>
            <MessageCircle className="w-5 h-5" style={{ color: "#fff", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }} />
          </div>
          <span className="text-[10px] text-white font-bold drop-shadow">{comments.length}</span>
        </button>
        <button onClick={shareToFeed} className="flex flex-col items-center gap-1" data-testid="reel-share">
          <div className="w-11 h-11 rounded-full bg-black/70 hover:bg-black/80 flex items-center justify-center border border-white/15">
            <Share2 className="w-5 h-5" style={{ color: "#fff", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }} />
          </div>
          <span className="text-[10px] text-white font-bold drop-shadow">Share</span>
        </button>

        {/* Viewer/commenter mini-avatar column */}
        {avatarUsers.length > 0 && (
          <div className="flex flex-col items-center gap-1.5 mt-2 max-h-[45vh] overflow-y-auto no-scrollbar" data-testid="reel-viewers">
            {avatarUsers.map((u) => (
              <a key={u.id} href={`/u/${u.id}`} title={u.name} className="block" data-testid={`reel-viewer-${u.id}`}>
                <img src={u.avatar} alt={u.name} className="w-8 h-8 rounded-full ring-1 ring-white/30 opacity-80 hover:opacity-100 transition" />
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Bottom content stack — sponsor banner, caption, comment composer, in that
          visual order (banner highest, composer lowest/closest to playback controls).
          flex-col-reverse means DOM order is composer-first so it anchors to the
          bottom of the container; caption and banner stack upward from there. This
          replaces three independently bottom-offset elements that used to land
          almost exactly on top of each other -- the comment input's own background
          was visually burying the caption text underneath it, which is why typed
          descriptions were invisible to every viewer. Also gives the sponsor banner
          (part of the 3-way ad revenue split) a permanent slot it never had in this
          full-screen reel view before, even though the same component already runs
          on Profile/Watch/VerticalFeed. */}
      <div className="absolute left-3 right-16 sm:right-20 bottom-16 sm:bottom-20 z-10 flex flex-col-reverse gap-2">
        {user && (
          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
            <input
              id="reel-comment-input"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") sendComment(); }}
              placeholder="Say something…"
              maxLength={200}
              className="flex-1 bg-black/35 backdrop-blur-md border border-white/15 rounded-full px-4 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:border-nexus-cyan"
              data-testid="reel-comment-input"
            />
            <button onClick={sendComment} className="bg-nexus-cyan text-black rounded-full w-10 h-10 flex items-center justify-center hover:bg-nexus-cyan/80 transition" data-testid="reel-comment-send" aria-label="Send comment">
              <Send className="w-4 h-4" />
            </button>
          </div>
        )}
        {reel.caption && (
          <div className="text-white text-sm font-medium pointer-events-none" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.9)" }} data-testid="reel-caption">
            {reel.caption}
          </div>
        )}
        <div onClick={(e) => e.stopPropagation()} data-testid="reel-sponsor-banner-wrap">
          <SponsorBanner surface="reel" creatorId={reel.user_id} />
        </div>
      </div>
      {/* Comment thread sheet — appears when the comment button in the right-side
          action column is tapped. Slides up from the bottom, shows every comment
          with author avatar + name + relative timestamp + text, with the composer
          docked inside so the user can reply without losing the thread. */}
      {commentsOpen && (
        <div
          className="absolute inset-x-0 bottom-0 z-20 bg-black/85 backdrop-blur-xl rounded-t-3xl border-t border-white/10 flex flex-col animate-[slideup_.25s_ease-out]"
          style={{ maxHeight: "62%" }}
          onClick={(e) => e.stopPropagation()}
          data-testid="reel-comments-sheet"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="text-[11px] tracking-[0.3em] uppercase font-display" style={{ color: "#fff" }}>
              Comments · {comments.length}
            </div>
            <button
              onClick={() => setCommentsOpen(false)}
              className="text-white/60 hover:text-white"
              data-testid="reel-comments-close"
              aria-label="Close comments"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {comments.length === 0 && (
              <div className="text-center text-xs py-8" style={{ color: "rgba(255,255,255,0.45)" }} data-testid="reel-comments-empty">
                No comments yet — say something first.
              </div>
            )}
            {comments.map((c) => (
              <div key={c.id} className="flex gap-2.5" data-testid={`reel-comment-${c.id}`}>
                <a href={`/u/${c.user_id}`} className="shrink-0">
                  <img
                    src={c.user_avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${c.user_id}`}
                    alt=""
                    className="w-8 h-8 rounded-full ring-1 ring-white/20"
                  />
                </a>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <a href={`/u/${c.user_id}`} className="text-[13px] font-medium truncate" style={{ color: "#fff" }}>
                      {c.user_name || "Someone"}
                    </a>
                    <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.45)" }}>
                      {relTime(c.ts)}
                    </span>
                  </div>
                  <div className="text-[13px] leading-snug mt-0.5 break-words" style={{ color: "rgba(255,255,255,0.9)" }}>
                    {c.text}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Docked composer inside the sheet */}
          {user && (
            <div className="border-t border-white/10 p-3 flex gap-2 bg-black/40">
              <input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") sendComment(); }}
                placeholder="Add a comment…"
                maxLength={200}
                className="flex-1 bg-white/5 border border-white/15 rounded-full px-4 py-2 text-sm placeholder-white/50 focus:outline-none focus:border-nexus-cyan"
                style={{ color: "#fff" }}
                data-testid="reel-comment-input-sheet"
              />
              <button
                onClick={sendComment}
                className="bg-nexus-cyan text-black rounded-full w-10 h-10 flex items-center justify-center hover:bg-nexus-cyan/80 transition shrink-0"
                data-testid="reel-comment-send-sheet"
                aria-label="Send comment"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          )}
          <style>{`@keyframes slideup { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
        </div>
      )}
      {interactorsKind && (
        <InteractorsModal
          postId={reel.id}
          initialKind={interactorsKind}
          counts={{ likes, views: viewCount, shares: reel.share_count || 0 }}
          onClose={() => setInteractorsKind(null)}
        />
      )}
    </div>
  );
}
